import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { PaginatedEventsResponse } from '@/api/services/event-cards-service';
import { withBasePath } from '@/utils/withBasePath';
import { getPageSize } from '../_shared';
import { fetchOrganizerEventsFromDate } from '../_shared/fetchEventsFromDate';

export function usePastEvents(organizerId: string, selectedDateFilter?: string | null) {
  const pageSize = useMemo(() => getPageSize(), []);
  const hasDateFilter = Boolean(selectedDateFilter);

  const query = useInfiniteQuery({
    queryKey: ['organizer-past-events', organizerId, pageSize, selectedDateFilter ?? 'all'],
    queryFn: ({ pageParam }) =>
      hasDateFilter
        ? fetchOrganizerEventsFromDate(
            organizerId,
            selectedDateFilter as string,
            typeof pageParam === 'number' ? pageParam : 1,
            pageSize,
            'start_desc',
          )
        : fetchPastEventsPage(organizerId, typeof pageParam === 'number' ? pageParam : 1, pageSize),
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? allPages.length + 1 : undefined),
  });

  const events = query.data?.pages.flatMap((p) => p.events) ?? [];
  const lastPage = query.data?.pages?.[query.data.pages.length - 1];
  const hasMorePages = Boolean(lastPage?.hasMore);

  return { ...query, events, hasMorePages };
}

async function fetchPastEventsPage(
  organizerId: string,
  page: number,
  pageSize: number,
): Promise<PaginatedEventsResponse> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const res = await fetch(withBasePath(`/api/organizers/${organizerId}/past-events/?${params}`), {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PaginatedEventsResponse;
}
